import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useInvoiceApi from "../../hooks/useInvoiceApi";
import { stepOneSchema } from "../../schemas/facturas";

/**
 * Componente para el paso 1 del wizard de facturas
 * Solicita RFC y código de servicio
 */
const StepRFC = ({ onContinue, initialValues = {} }) => {
  // Estado para el servicio
  const [serviceInfo, setServiceInfo] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [serviceError, setServiceError] = useState(null);

  // API para verificar servicios
  const { verifyServiceCode } = useInvoiceApi();

  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(stepOneSchema),
    mode: "onChange",
    defaultValues: {
      rfc: initialValues.rfc || "",
      serviceCode: initialValues.serviceCode || "",
    },
  });

  // Auto-convertir a mayúsculas
  const handleInputChange = (e) => {
    e.target.value = e.target.value.toUpperCase();
  };

  // En modo desarrollo/demostración, no necesitamos verificar el código de servicio
  // Sin embargo, mantenemos esta variable para compatibilidad
  const watchServiceCode = watch("serviceCode");

  // Efecto para verificar el código de servicio cuando cambia y es válido
  useEffect(() => {
    const checkServiceCode = async () => {
      // Solo verificar si el código de servicio cumple con el formato
      if (watchServiceCode && watchServiceCode.match(/^[A-Z0-9-]{6,20}$/)) {
        setIsVerifying(true);
        setServiceError(null);

        try {
          // Códigos de servicio de demostración (para desarrollo)
          const demoServices = {
            "SERV-12345": {
              descripcion: "Consulta cardiológica general",
              valor_unitario: 800.0,
            },
            "SERV-67890": {
              descripcion: "Electrocardiograma de 12 derivaciones",
              valor_unitario: 1200.0,
            },
            "ECOCAR-123": {
              descripcion: "Ecocardiograma transtorácico",
              valor_unitario: 2500.0,
            },
            "HOLTER-456": {
              descripcion: "Monitoreo Holter de 24 horas",
              valor_unitario: 1800.0,
            },
          };

          // Verificar primero en códigos de demostración
          if (demoServices[watchServiceCode]) {
            setServiceInfo(demoServices[watchServiceCode]);
            return;
          }

          // Si no es un código de demostración, intenta verificar con el backend
          const result = await verifyServiceCode(watchServiceCode);
          if (result.valid) {
            setServiceInfo(result.service);
          } else {
            setServiceError("Código de servicio no encontrado");
          }
        } catch (error) {
          console.error("Error al verificar el código:", error);
          // En modo desarrollo, acepta ciertos códigos aunque haya error en API
          if (
            ["SERV-12345", "SERV-67890", "ECOCAR-123", "HOLTER-456"].includes(
              watchServiceCode
            )
          ) {
            setServiceError(null);
            setServiceInfo({
              descripcion: "Servicio simulado (modo desarrollo)",
              valor_unitario: 1000.0,
            });
          } else {
            setServiceError("Error al verificar el código de servicio");
          }
        } finally {
          setIsVerifying(false);
        }
      } else {
        setServiceInfo(null);
        setServiceError(null);
      }
    };

    // Usar un timer para no hacer demasiadas peticiones mientras el usuario escribe
    const timer = setTimeout(checkServiceCode, 800);
    return () => clearTimeout(timer);
  }, [watchServiceCode, verifyServiceCode]);

  // Manejar el envío del formulario - Simplificado para pruebas
  const onSubmit = (data) => {
    // Datos simulados para demostración
    const demoServices = {
      "SERV-12345": {
        descripcion: "Consulta cardiológica general",
        valor_unitario: 800.0,
      },
      "SERV-67890": {
        descripcion: "Electrocardiograma de 12 derivaciones",
        valor_unitario: 1200.0,
      },
      "ECOCAR-123": {
        descripcion: "Ecocardiograma transtorácico",
        valor_unitario: 2500.0,
      },
      "HOLTER-456": {
        descripcion: "Monitoreo Holter de 24 horas",
        valor_unitario: 1800.0,
      },
    };

    // Asignar datos de servicio (usar datos conocidos o valores por defecto)
    const serviceData = demoServices[data.serviceCode] || {
      descripcion: "Servicio médico (simulación)",
      valor_unitario: 1000.0,
    };

    console.log("Enviando datos al siguiente paso:", {
      ...data,
      serviceInfo: serviceData,
      descripcion_servicio: serviceData.descripcion,
      valor_unitario: serviceData.valor_unitario,
    });

    // Avanzar al siguiente paso con los datos simulados
    onContinue({
      ...data,
      serviceInfo: serviceData,
      descripcion_servicio: serviceData.descripcion,
      valor_unitario: serviceData.valor_unitario,
    });
  };

  // En modo desarrollo, no necesitamos validar el formulario
  // El botón siempre estará activo para fines de prueba

  return (
    <div className="w-full max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden p-6 md:p-8">
      <h2 className="text-2xl font-bold text-primary mb-6 text-center">
        Genera tu factura
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Campo RFC */}
        <div>
          <label
            htmlFor="rfc"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            RFC
          </label>
          <input
            id="rfc"
            type="text"
            {...register("rfc")}
            onChange={handleInputChange}
            placeholder="Ejemplo: XAXX010101000"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
              errors.rfc
                ? "border-red-500 focus:ring-red-200"
                : "border-gray-300 focus:ring-primary/20 focus:border-primary"
            }`}
          />
          {errors.rfc && (
            <p className="mt-1 text-sm text-red-600">{errors.rfc.message}</p>
          )}
        </div>

        {/* Campo Código de Servicio */}
        <div>
          <label
            htmlFor="serviceCode"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Código de servicio
          </label>
          <input
            id="serviceCode"
            type="text"
            {...register("serviceCode")}
            onChange={handleInputChange}
            placeholder="Ejemplo: SERV-12345"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none transition-colors ${
              errors.serviceCode || serviceError
                ? "border-red-500 focus:ring-red-200"
                : serviceInfo
                ? "border-green-500 focus:ring-green-200"
                : "border-gray-300 focus:ring-primary/20 focus:border-primary"
            }`}
          />
          <p className="mt-1 text-xs text-gray-500">
            Viene en el ticket entregado por cardiologíadelnorte
          </p>
          {errors.serviceCode && (
            <p className="mt-1 text-sm text-red-600">
              {errors.serviceCode.message}
            </p>
          )}
          {serviceError && !errors.serviceCode && (
            <p className="mt-1 text-sm text-red-600">{serviceError}</p>
          )}
          {serviceInfo && !errors.serviceCode && !serviceError && (
            <p className="mt-1 text-sm text-green-600">
              Servicio encontrado: {serviceInfo.descripcion} - $
              {serviceInfo.valor_unitario.toFixed(2)}
            </p>
          )}
          {isVerifying && (
            <p className="mt-1 text-sm text-blue-600">Verificando código...</p>
          )}
        </div>

        {/* Botón Continuar - Siempre habilitado en modo desarrollo para pruebas */}
        <button
          type="submit"
          disabled={false}
          className="w-full py-2 px-4 rounded-lg font-semibold transition-all duration-300 bg-primary text-white hover:bg-primary/90 shadow-md hover:shadow-lg"
        >
          Continuar
        </button>
      </form>
    </div>
  );
};

export default StepRFC;
